/**
 * sdx-cart.js — Native Shopify cart interactions.
 * No jQuery. Uses fetch() against /cart/*.js endpoints.
 * Dispatches standard `cart:change` CustomEvent so cart-drawer components (Horizon)
 * can listen without coupling.
 */
(function () {
  'use strict';

  const ENDPOINTS = {
    get: '/cart.js',
    add: '/cart/add.js',
    change: '/cart/change.js',
    update: '/cart/update.js',
    clear: '/cart/clear.js'
  };

  async function request(endpoint, body) {
    const opts = {
      method: body ? 'POST' : 'GET',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    };
    if (body) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    const res = await fetch(endpoint, opts);
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data && data.description || 'Cart error');
      err.status = res.status;
      err.payload = data;
      throw err;
    }
    return data;
  }

  function emit(cart) {
    document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart } }));
  }

  const SdxCart = {
    async get() {
      const cart = await request(ENDPOINTS.get);
      emit(cart);
      return cart;
    },
    async add(items) {
      const payload = Array.isArray(items) ? { items } : items;
      const res = await request(ENDPOINTS.add, payload);
      const cart = await this.get();
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { added: res, cart } }));
      return res;
    },
    async change(key, quantity) {
      const cart = await request(ENDPOINTS.change, { id: key, quantity });
      emit(cart);
      return cart;
    },
    async update(updates) {
      const cart = await request(ENDPOINTS.update, { updates });
      emit(cart);
      return cart;
    },
    async clear() {
      const cart = await request(ENDPOINTS.clear);
      emit(cart);
      return cart;
    }
  };

  window.SdxCart = SdxCart;

  // Toast helper — shows brief success/error message
  function showToast(text, kind) {
    let wrap = document.querySelector('[data-sdx-toast-wrap]');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.setAttribute('data-sdx-toast-wrap', '');
      wrap.style.cssText = 'position:fixed;right:20px;bottom:20px;z-index:9999;display:flex;flex-direction:column;gap:8px;pointer-events:none;';
      document.body.appendChild(wrap);
    }
    const t = document.createElement('div');
    t.setAttribute('role', 'status');
    t.style.cssText = 'pointer-events:auto;background:' + (kind === 'err' ? '#c0392b' : '#1f3a2b') + ';color:#fff;padding:12px 18px;border-radius:12px;font-size:0.92rem;box-shadow:0 8px 24px rgba(0,0,0,.12);opacity:0;transform:translateY(8px);transition:opacity 200ms ease,transform 200ms ease;max-width:320px;';
    t.textContent = text;
    wrap.appendChild(t);
    requestAnimationFrame(() => { t.style.opacity = '1'; t.style.transform = 'translateY(0)'; });
    setTimeout(() => {
      t.style.opacity = '0';
      t.style.transform = 'translateY(8px)';
      setTimeout(() => t.remove(), 220);
    }, 2600);
  }

  // Update header cart count badge
  function updateCartCount(cart) {
    const count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
    document.querySelectorAll('[data-sdx-cart-count]').forEach((el) => {
      el.textContent = count;
      el.toggleAttribute('hidden', count === 0);
    });
  }

  document.addEventListener('cart:change', (e) => updateCartCount(e.detail && e.detail.cart));
  document.addEventListener('cart:added', (e) => updateCartCount(e.detail && e.detail.cart));

  // Bind <form action="/cart/add"> submit handlers
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action$="/cart/add"][data-sdx-ajax]');
    if (!form) return;
    e.preventDefault();
    // Re-entrancy guard — single add per submit, even if another listener fires.
    if (form.dataset.sdxSubmitting === '1') return;
    form.dataset.sdxSubmitting = '1';
    const fd = new FormData(form);
    const obj = {};
    for (const [k, v] of fd.entries()) {
      // /cart/add.js only accepts id, quantity, properties{} — strip options[X] UI-only fields
      if (k.startsWith('options[')) continue;
      obj[k] = v;
    }
    const submitBtn = form.querySelector('button[type="submit"]');
    const originalLabel = submitBtn ? submitBtn.innerHTML : '';
    try {
      form.setAttribute('data-loading', 'true');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
        submitBtn.innerHTML = '<span style="display:inline-flex;align-items:center;gap:8px;"><span style="width:14px;height:14px;border:2px solid currentColor;border-top-color:transparent;border-radius:50%;display:inline-block;animation:sdx-spin 600ms linear infinite;"></span>Ekleniyor...</span>';
      }
      await SdxCart.add(obj);
      form.dispatchEvent(new CustomEvent('sdx:add-success', { bubbles: true }));
      showToast('Sepete eklendi', 'ok');
      if (submitBtn) {
        submitBtn.innerHTML = '<span style="display:inline-flex;align-items:center;gap:8px;">✓ Eklendi</span>';
        setTimeout(() => { submitBtn.innerHTML = originalLabel; submitBtn.disabled = false; submitBtn.removeAttribute('aria-busy'); }, 1600);
      }
    } catch (err) {
      form.dispatchEvent(new CustomEvent('sdx:add-error', { bubbles: true, detail: err }));
      showToast((err && err.message) || 'Sepete eklenemedi', 'err');
      if (submitBtn) {
        submitBtn.innerHTML = originalLabel;
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
      }
    } finally {
      form.removeAttribute('data-loading');
      delete form.dataset.sdxSubmitting;
    }
  });

  // Inject spinner keyframe once
  if (!document.getElementById('sdx-cart-anim')) {
    const st = document.createElement('style');
    st.id = 'sdx-cart-anim';
    st.textContent = '@keyframes sdx-spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(st);
  }

  // Initial cart fetch to sync badge on page load
  document.addEventListener('DOMContentLoaded', () => {
    SdxCart.get().catch(() => {});
  });

  // Bind [data-sdx-qty] inputs and +/- buttons inside cart
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-sdx-qty-step]');
    if (!btn) return;
    const key = btn.getAttribute('data-sdx-key');
    const step = parseInt(btn.getAttribute('data-sdx-qty-step'), 10) || 0;
    if (!key) return;
    const row = btn.closest('[data-sdx-cart-item]');
    const current = parseInt(row && row.querySelector('[data-sdx-qty-input]').value, 10) || 1;
    const next = Math.max(0, current + step);
    try {
      await SdxCart.change(key, next);
    } catch (err) { console.error('[sdx-cart]', err); }
  });
})();
